import { useMemo, useState } from 'react';
import { adminStore } from '../../../../stores';
import { amountFormatter, toSentenceCase } from '../../../../utils/utils';
import styles from '../../../../assets/styles/Admin/AdminProducts.module.scss';

export const useAdminSingleProductLogic = () => {
	const { selectedProduct } = adminStore;
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const openModalHandler = () => {
		setDeleteModalOpen(true);
	};

	const closeModal = () => {
		setDeleteModalOpen(false);
	};

	const {
		description,
		imageUrls = [],
		name,
		variations,
		type,
		_id,
		gender,
		pricingModel,
		styleCollections,
		topView,
		bestSelling,
		daysRequiredForDelivery,
	} = selectedProduct || {};

	const { early, late } = daysRequiredForDelivery || {};

	const formatAmount = (currencyCode, value) =>
		Number(value) > 0
			? amountFormatter(currencyCode).format(Number(value))
			: 'N/A';

	const variationColumns = useMemo(
		() => [
			{
				Header: 'Variation Name',
				accessor: 'name',
			},
			{
				Header: 'Type',
				accessor: 'isDigital',
				Cell: ({ cell }) => {
					const isDigital = cell.row.original.isDigital;
					return <span>{isDigital ? 'Digital' : 'Physical'}</span>;
				},
			},
			{
				Header: 'Pricing',
				accessor: 'sellingPrices',
				disableSortBy: true,
				Cell: ({ cell }) => {
					const sellingPrices = cell.row.original.sellingPrices || [];

					if (sellingPrices.length === 0) {
						return <span>N/A</span>;
					}

					return (
						<div className={styles.singleProduct__prices}>
							{sellingPrices.map(price => {
								const label =
									price?.type === 'LOCAL' ? 'Local' : 'International';
								const rtw = formatAmount(
									price?.currencyCode,
									price?.withMaterial
								);
								const promo = formatAmount(
									price?.currencyCode,
									price?.onSaleSlashPrice
								);
								const customerMaterial = formatAmount(
									price?.currencyCode,
									price?.withoutMaterial
								);

								return (
									<div key={price?._id || `${label}-${price?.currencyCode}`}>
										<p>
											<b>{label}:</b>
										</p>{' '}
										<b>RTW </b> {rtw} | <b>Promo </b> {promo} |{' '}
										<b>With Customer Material </b> {customerMaterial}
									</div>
								);
							})}
						</div>
					);
				},
			},

			{
				Header: 'Availability',
				accessor: 'isSoldOut',
				Cell: ({ cell }) => {
					const isSoldOut = cell.row.original.isSoldOut;
					return <span>{isSoldOut ? 'Sold Out' : 'Available'}</span>;
				},
			},
			{
				Header: 'Sizes',
				Cell: ({ cell }) => {
					const sizes = cell.row.original.sizes;
					return <span>{sizes?.length > 0 ? sizes.join(', ') : 'N/A'}</span>;
				},
			},

			{
				Header: 'Colors',
				Cell: ({ cell }) => {
					const colors = cell.row.original.colorsAvailable;
					return (
						<span>
							{colors?.length > 0 ? colors.map(c => c.color).join(', ') : 'N/A'}
						</span>
					);
				},
			},
			{
				Header: 'Accessories',
				Cell: ({ cell }) => {
					const accessories = cell.row.original.accessories;
					return (
						<span>
							{accessories?.length > 0 ? accessories.join(', ') : 'N/A'}
						</span>
					);
				},
			},
		],
		[]
	);

	const otherDetails = [
		{
			label: 'Type',
			value: type,
		},
		{
			label: 'Gender',
			value: gender,
		},
		{
			label: 'Pricing Model',
			value: toSentenceCase(pricingModel),
		},
		{
			label: 'Collections',
			value: styleCollections?.join(', ') || 'N/A',
		},
		{
			label: 'Product positioning',
			value: `This product shows up at the ${topView ? 'Top' : 'Regular'} position  ${bestSelling ? 'and is tagged as Best selling' : ''}`,
		},
		{
			label: 'Delivery Duration',
			value: `${early} - ${late} days`,
		},
	];

	const otherDetailsColumns = [
		{
			Header: 'Attribute',
			accessor: 'label',
		},
		{
			Header: 'Value',
			accessor: 'value',
		},
	];

	return {
		name,
		description,
		imageUrls,
		type,
		_id,
		variations,
		variationColumns,
		otherDetails,
		otherDetailsColumns,
		openModalHandler,
		closeModal,
		deleteModalOpen,
	};
};

export default useAdminSingleProductLogic;
